import {
  all,
  call,
  put,
  takeEvery,
  takeLatest,
  select,
} from 'redux-saga/effects';
import {
  fetchByIdMethodMapping,
  fetchMethodMapping,
} from '../../../common/constants/EntityActions';
import envConfig from '../../../config';

import {
  sendEmail,
  replyToEmail,
  addActivity,
  fetchActivities,
  addNote,
  fetchNotes,
  markPulse,
  bookmarkNote,
  bookmarkPulse,
  bookmarkActivity,
  deleteNote,
  deleteActivity,
  deletePulse,
  updateActivity,
  updateNote,
  updatePulse,
  fetchEmail,
  removeFile,
  fetchOrganizationDataById,
  fetchAccountDataById,
  fetchContactDataById,
  fetchFilesTimeline,
  refreshPulse,
  refreshActivities,
  refreshNotes,
} from '../../routines';
import { request } from '../../../common/util/RestClient';
import SUCCESS_MESSAGES from '../../../common/constants/SuccessMessagesConstants';
import { selectLoggedInUser } from '../../selectors/userSelector';
import { fetchEmailsForThread } from '../../routines/emailRoutines';
import { SUCCESS_PLAN_LEVEL } from '../../../common/constants/SuccessPlanConstants';
import { GTM_TRACK_EVENT } from '../../../common/GoogleTagManager/GoogleTagManager';
import { GTM_EVENTS } from '../../../common/GoogleTagManager/GTMEventConstants';

export function* sendEmailSaga({ payload }) {
  try {
    GTM_TRACK_EVENT(GTM_EVENTS.ACTIONS_360_EMAIL);
    const { userId } = yield select(selectLoggedInUser);
    const URL = `${envConfig.APIG_URL}email-v3/users/${userId}/email-threads`;

    const { level, entityId, email } = payload;
    const { integrationEmail, ...emailRest } = email;

    const config = {
      method: 'POST',
      payload: emailRest,
    };
    const response = yield call(request, URL, config);
    yield put(
      sendEmail.success({
        response,
        successMessage: 'Email sent successfully',
      })
    );
    yield put(fetchEmail({ level, entityId }));
    yield put(fetchFilesTimeline({ level, entityId }));
    yield put(fetchActivities.trigger({ level, entityId }));
    yield put(removeFile({ clearAll: true }));
  } catch (error) {
    yield put(sendEmail.failure({ error }));
  }
}
export function* replyToEmailSaga({ payload }) {
  try {
    GTM_TRACK_EVENT(GTM_EVENTS.ACTIONS_360_EMAIL);
    const { level, entityId, threadId, email } = payload;

    const { userId } = yield select(selectLoggedInUser);
    const URL = `${envConfig.APIG_URL}email-v3/users/${userId}/email-threads/${threadId}`;
    const { integrationEmail, ...emailRest } = email;
    const config = {
      method: 'PATCH',
      payload: emailRest,
    };
    const response = yield call(request, URL, config);
    yield put(
      replyToEmail.success({
        response,
        successMessage: 'Email sent successfully',
      })
    );
    yield put(fetchEmailsForThread({ level, entityId, threadId }));
    yield put(fetchFilesTimeline({ level, entityId }));
    yield put(fetchActivities.trigger({ level, entityId }));
  } catch (error) {
    yield put(replyToEmail.failure({ error }));
  }
}

export function* addActivitySaga({ payload }) {
  try {
    GTM_TRACK_EVENT(GTM_EVENTS.ACTIONS_360_ACTIVITIES);
    const { formData } = payload;
    const URL = `${envConfig.APIG_URL}core/entity-action/activity?text_format=rich`;
    const config = {
      method: 'POST',
      payload: formData,
    };
    const response = yield call(request, URL, config);
    yield put(
      addActivity.success({
        response,
        successMessage: SUCCESS_MESSAGES.ADD_ACTIVITY,
      })
    );
    yield put(refreshActivities.trigger(Math.random()));
  } catch (err) {
    yield put(addActivity.failure({ error: err }));
  }
}

export function* updateActivitySaga({ payload }) {
  try {
    const { activityId, formData } = payload;
    const URL = `${envConfig.APIG_URL}core/entity-action/activity/${activityId}?text_format=rich`;
    const config = {
      method: 'PUT',
      payload: formData,
    };
    const response = yield call(request, URL, config);
    yield put(
      updateActivity.success({
        activityId,
        response,
        successMessage: SUCCESS_MESSAGES.UPDATE_ACTIVITY,
      })
    );
  } catch (err) {
    yield put(
      updateActivity.failure({ activityId: payload.activityId, error: err })
    );
  }
}

export function* bookmarkActivitySaga({ payload }) {
  try {
    const { activityId, formData } = payload;
    const URL = `${envConfig.APIG_URL}core/entity-action/activity/${activityId}/bookmark`;
    const { bookmarkStatus: bookmarked } = formData;
    const body = {
      bookmarkStatus: bookmarked,
    };
    const config = {
      method: 'PATCH',
      payload: body,
    };
    const response = yield call(request, URL, config);
    yield put(
      bookmarkActivity.success({
        response,
        bookmarked,
        successMessage:
          bookmarked === 1
            ? SUCCESS_MESSAGES.BOOKMARK_Activity
            : SUCCESS_MESSAGES.REMOVE_BOOKMARK_Activity,
      })
    );
  } catch (err) {
    yield put(bookmarkActivity.failure({ error: err }));
  }
}

export function* deleteActivitySaga({ payload }) {
  try {
    const { activityId } = payload;
    const URL = `${envConfig.APIG_URL}core/entity-action/activity/${activityId}`;
    const response = yield call(request, URL, {
      method: 'DELETE',
    });
    yield put(
      deleteActivity.success({
        response,
        successMessage: SUCCESS_MESSAGES.DELETE_ACTIVITY,
      })
    );
    yield put(refreshActivities.trigger(Math.random()));
  } catch (err) {
    yield put(deleteActivity.failure({ error: err }));
  }
}

export function* addNoteSaga({ payload }) {
  try {
    const { formData, source } = payload;
    const URL = `${envConfig.APIG_URL}core/notes`;
    const config = {
      method: 'POST',
      payload: { ...formData, source },
    };
    const response = yield call(request, URL, config);
    yield put(
      addNote.success({ response, successMessage: SUCCESS_MESSAGES.ADD_NOTE })
    );
    if (formData?.linkedEntities?.success_plans) {
      yield put(
        fetchNotes.trigger({
          level: SUCCESS_PLAN_LEVEL,
          entityId: parseInt(formData.linkedEntities.success_plans[0], 10),
        })
      );
    } else {
      yield put(refreshNotes.trigger(Math.random()));
    }
  } catch (err) {
    yield put(addNote.failure({ error: err }));
  }
}

export function* updateNoteSaga({ payload }) {
  try {
    const { noteId, formData } = payload;
    const URL = `${envConfig.APIG_URL}core/notes/${noteId}`;
    const config = {
      method: 'PUT',
      payload: formData,
    };
    const response = yield call(request, URL, config);
    yield put(
      updateNote.success({
        noteId,
        response,
        successMessage: SUCCESS_MESSAGES.UPDATE_NOTE,
      })
    );
  } catch (err) {
    yield put(updateNote.failure({ noteId: payload.noteId, err }));
  }
}

export function* bookmarkNoteSaga({ payload }) {
  try {
    const { noteId, formData } = payload;
    const URL = `${envConfig.APIG_URL}core/notes/${noteId}/bookmark`;
    const { bookmarkStatus: bookmarked } = formData;
    const body = {
      bookmarkStatus: bookmarked,
    };
    const config = {
      method: 'PATCH',
      payload: body,
    };
    const response = yield call(request, URL, config);
    yield put(
      bookmarkNote.success({
        response,
        bookmarked,
        successMessage:
          bookmarked === 1
            ? SUCCESS_MESSAGES.BOOKMARK_NOTE
            : SUCCESS_MESSAGES.REMOVE_BOOKMARK_NOTE,
      })
    );
    // yield put(fetchNotes.trigger({ level, entityId }));
  } catch (err) {
    yield put(bookmarkNote.failure({ error: err }));
  }
}

export function* deleteNoteSaga({ payload }) {
  try {
    const { noteId } = payload;
    const URL = `${envConfig.APIG_URL}core/notes/${noteId}`;
    const response = yield call(request, URL, {
      method: 'DELETE',
    });
    yield put(
      deleteNote.success({
        response,
        successMessage: SUCCESS_MESSAGES.DELETE_NOTE,
      })
    );
    yield put(refreshNotes.trigger(Math.random()));
  } catch (err) {
    yield put(deleteNote.failure({ error: err }));
  }
}

export function* markPulseSaga({ payload }) {
  try {
    const { level, formData, scope } = payload;
    const { parentIdentifier } = formData;
    const URL = `${envConfig.APIG_URL}core/entity-action/pulse?level=${level}`;
    const config = {
      method: 'POST',
      payload: formData,
    };
    const response = yield call(request, URL, config);
    yield put(
      markPulse.success({
        response,
        successMessage: SUCCESS_MESSAGES.ADD_CSM_PULSE,
      })
    );
    if (scope && scope === 'ENTITY_360') {
      yield put(refreshPulse.trigger(Math.random()));
      yield put(
        fetchByIdMethodMapping[level].trigger({
          [`${level}Id`]: parentIdentifier,
        })
      );
    } else if (scope && scope === 'ENTITY_LIST') {
      yield put(fetchMethodMapping[level].trigger({ usePrevPayload: true }));
    }
  } catch (err) {
    yield put(markPulse.failure({ error: err }));
  }
}

export function* updatePulseSaga({ payload }) {
  try {
    const { level, pulseId, formData } = payload;
    const { parentIdentifier, ..._data } = formData;
    const URL = `${envConfig.APIG_URL}core/entity-action/pulse/${pulseId}?entityId=${parentIdentifier}&level=${level}`;
    const config = {
      method: 'PUT',
      payload: _data,
    };
    const response = yield call(request, URL, config);
    yield put(
      updatePulse.success({
        pulseId,
        response,
        successMessage: SUCCESS_MESSAGES.UPDATE_CSM_PULSE,
      })
    );

    const fetchRoutines = {
      organization: fetchOrganizationDataById,
      account: fetchAccountDataById,
      contact: fetchContactDataById,
    };
    const entityIds = {
      organization: 'organizationId',
      account: 'accountId',
      contact: 'contactId',
    };
    yield put(
      fetchRoutines[level].trigger({
        [`${entityIds[level]}`]: parentIdentifier,
      })
    );
  } catch (err) {
    yield put(updatePulse.failure({ pulseId: payload.pulseId, error: err }));
  }
}

export function* bookmarkPulseSaga({ payload }) {
  try {
    const { pulseId, formData } = payload;
    const URL = `${envConfig.APIG_URL}core/entity-action/pulse/${pulseId}/bookmark`;
    const { bookmarkStatus: bookmarked } = formData;
    const body = {
      bookmarkStatus: bookmarked,
    };
    const config = {
      method: 'PATCH',
      payload: body,
    };
    const response = yield call(request, URL, config);
    yield put(
      bookmarkPulse.success({
        response,
        bookmarked,
        successMessage:
          bookmarked === 1
            ? SUCCESS_MESSAGES.BOOKMARK_CSM_PULSE
            : SUCCESS_MESSAGES.REMOVE_BOOKMARK_CSM_PULSE,
      })
    );
  } catch (err) {
    yield put(bookmarkPulse.failure({ error: err }));
  }
}

export function* deletePulseSaga({ payload }) {
  try {
    const { level, entityId: parentIdentifier, pulseId } = payload;
    const URL = `${envConfig.APIG_URL}core/entity-action/pulse/${pulseId}?entityId=${parentIdentifier}&level=${level}`;
    const response = yield call(request, URL, {
      method: 'DELETE',
    });
    yield put(
      deletePulse.success({
        response,
        successMessage: SUCCESS_MESSAGES.DELETE_CSM_PULSE,
      })
    );

    const fetchRoutines = {
      organization: fetchOrganizationDataById,
      account: fetchAccountDataById,
      contact: fetchContactDataById,
    };

    const entityIds = {
      organization: 'organizationId',
      account: 'accountId',
      contact: 'contactId',
    };
    yield put(
      fetchRoutines[level].trigger({
        [`${entityIds[level]}`]: parentIdentifier,
      })
    );

    yield put(refreshPulse.trigger(Math.random()));
  } catch (err) {
    yield put(deletePulse.failure({ error: err }));
  }
}
export async function validateExternalIdExist({
  level,
  externalId,
  objectLevel,
}) {
  let URL = `${envConfig.APIG_URL}core/entity?levels=${level}`;
  if (externalId) {
    URL = `${URL}&externalId=${encodeURIComponent(externalId)}`;
  } else {
    throw new Error('No Value Entered!');
  }

  if (objectLevel) {
    URL = `${URL}&objectLevel=${encodeURIComponent(objectLevel)}`;
  }

  try {
    const response = await request(URL);
    return response;
  } catch (error) {
    return null;
  }
}
export default function* root() {
  yield all([takeLatest(sendEmail.TRIGGER, sendEmailSaga)]);
  yield all([takeLatest(replyToEmail.TRIGGER, replyToEmailSaga)]);
  yield all([takeLatest(addActivity.TRIGGER, addActivitySaga)]);
  yield all([takeLatest(bookmarkActivity.TRIGGER, bookmarkActivitySaga)]);
  yield all([takeLatest(deleteActivity.TRIGGER, deleteActivitySaga)]);
  yield all([takeLatest(addNote.TRIGGER, addNoteSaga)]);
  yield all([takeLatest(bookmarkNote.TRIGGER, bookmarkNoteSaga)]);
  yield all([takeLatest(deleteNote.TRIGGER, deleteNoteSaga)]);
  yield all([takeLatest(markPulse.TRIGGER, markPulseSaga)]);
  yield all([takeLatest(bookmarkPulse.TRIGGER, bookmarkPulseSaga)]);
  yield all([takeLatest(deletePulse.TRIGGER, deletePulseSaga)]);
  yield all([takeEvery(updatePulse.TRIGGER, updatePulseSaga)]);
  yield all([takeEvery(updateActivity.TRIGGER, updateActivitySaga)]);
  yield all([takeEvery(updateNote.TRIGGER, updateNoteSaga)]);
}
